import React from 'react';
import { Link } from 'react-router-dom';
import TopNav from './TopNav';
import SolutionsFooter from './SolutionsFooter';
import HowToEngage from './HowToEngage';
import useSEO from '../hooks/useSEO';

function LogisticsTransportationExample() {
  useSEO({
    title: 'Logistics & Transportation Simulation',
    description: 'Simulate routes, fleet capacity, and demand surges. Cut delivery costs and hit every delivery window.'
  });

  return (
    <div className="min-h-screen bg-white">
      <TopNav />

      <main className="max-w-5xl mx-auto px-8 py-16 md:py-24">
        <div className="text-center mb-16">
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Route + Fleet Optimization</p>
          <h1 className="text-4xl md:text-5xl font-medium text-gray-900 mb-6 leading-tight tracking-tight">Cut delivery costs and hit every window</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">Simulate routes, fleet capacity, and demand surges to optimize every mile and minimize late deliveries.</p>
        </div>

        <div className="mb-16">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">Who this is for</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"><p className="font-medium text-gray-900 mb-2">Logistics Directors</p><p className="text-sm text-gray-600">Reducing cost per delivery while improving service</p></div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"><p className="font-medium text-gray-900 mb-2">Fleet Managers</p><p className="text-sm text-gray-600">Optimizing vehicle utilization and routing</p></div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"><p className="font-medium text-gray-900 mb-2">Operations Leaders</p><p className="text-sm text-gray-600">Planning capacity for seasonal demand</p></div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">Decisions this improves</h2>
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">How many vehicles to deploy each day</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">Which routes to prioritize during disruptions</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">Where to position inventory for fastest delivery</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">When to use owned fleet vs. third-party carriers</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">How demand surges affect delivery windows</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">Trade-offs between speed, cost, and reliability</p></div>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <div className="bg-gray-900 text-white rounded-lg p-8 md:p-10">
            <p className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">Example: Regional Distribution</p>
            <p className="text-lg leading-relaxed mb-6">A regional distributor simulated driver schedules, vehicle capacity constraints, and real-time traffic patterns. The model identified that consolidating three distribution points into two—while adjusting delivery windows by 30 minutes—would reduce fuel costs by 18% and improve on-time delivery to 97%.</p>
            <p className="text-gray-400 text-sm">The impact: $1.2M annual savings with improved customer satisfaction scores.</p>
          </div>
        </div>

                <HowToEngage
          platformDescription="Model your delivery network, test routing scenarios, and share insights with operations. Ideal for logistics teams who want ongoing optimization capability."
          consultingDescription="Partner with our logistics consultants to build a custom network model, run optimization workshops, and deliver implementation-ready recommendations."
        />

        <div className="pt-8 border-t border-gray-200">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6">Related solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/solutions/supply-chain" className="text-gray-700 hover:text-gray-900 text-sm">Inventory + Production →</Link>
            <Link to="/solutions/retail-ecommerce" className="text-gray-700 hover:text-gray-900 text-sm">Demand Forecasting + Pricing →</Link>
            <Link to="/solutions/mining-manufacturing" className="text-gray-700 hover:text-gray-900 text-sm">Asset Uptime + Maintenance →</Link>
          </div>
        </div>
      </main>

      <SolutionsFooter />
    </div>
  );
}

export default LogisticsTransportationExample;
